// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../services/firebase';
import {
  ADMIN_AUTH_POLICY,
  canUseLegacyAdminLogin,
  isAdminEmail,
  matchesLegacyAdminCredentials,
} from '../features/auth/authPolicy';
import {
  isValidEmail,
  validateUserSession as isValidUserSession,
} from '../features/auth/authValidation';
import {
  toLoginErrorMessage,
  toRegisterErrorMessage,
  toResetPasswordErrorMessage,
} from '../features/auth/authErrorMessages';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const syncAdminState = (user) => {
    setIsAdmin(isAdminEmail(user?.email));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      syncAdminState(user);
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Security: Enhanced admin account creation with proper validation
  const createAdminAccount = async () => {
    if (!canUseLegacyAdminLogin()) {
      throw new Error('Административният вход с кратко име не е конфигуриран.');
    }

    try {
      // First try to sign in to check if admin exists
      await signInWithEmailAndPassword(
        auth,
        ADMIN_AUTH_POLICY.email,
        ADMIN_AUTH_POLICY.legacyPassword
      );
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        try {
          // Create admin account only if it doesn't exist
          await createUserWithEmailAndPassword(
            auth,
            ADMIN_AUTH_POLICY.email,
            ADMIN_AUTH_POLICY.legacyPassword
          );
        } catch (createError) {
          console.error('Error creating admin account:', createError);
          throw new Error('Failed to create admin account');
        }
      } else {
        console.error('Error checking admin account:', error);
        throw error;
      }
    }
  };

  function register(email, password) {
    // Security: Validate email format
    if (!isValidEmail(email)) {
      throw new Error('Невалиден имейл формат.');
    }

    // Security: Prevent registration of admin email
    if (isAdminEmail(email)) {
      throw new Error('Този имейл е резервиран за системата.');
    }

    return createUserWithEmailAndPassword(auth, email, password).catch((error) => {
      throw new Error(toRegisterErrorMessage(error));
    });
  }

  async function login(email, password) {
    // Security: Input validation
    if (!email || !password) {
      throw new Error('Моля, попълнете всички полета.');
    }

    // Security: Handle admin login more securely
    if (matchesLegacyAdminCredentials({ identifier: email, password })) {
      try {
        // Ensure admin account exists
        await createAdminAccount();
        
        // Sign in with actual admin credentials
        const userCredential = await signInWithEmailAndPassword(
          auth,
          ADMIN_AUTH_POLICY.email,
          ADMIN_AUTH_POLICY.legacyPassword
        );
        
        // Validate the user session
        if (!isValidUserSession(userCredential.user)) {
          throw new Error('Невалидна потребителска сесия.');
        }
        
        syncAdminState(userCredential.user);
        return userCredential;
      } catch (error) {
        console.error('Admin login error:', error);
        throw new Error(
          error.code ? 'Неуспешен вход като администратор.' : error.message
        );
      }
    }

    // Security: Regular user login with validation
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Validate the user session
      if (!isValidUserSession(userCredential.user)) {
        throw new Error('Невалидна потребителска сесия.');
      }
      
      // Security: Ensure regular users don't get admin privileges
      syncAdminState(userCredential.user);
      
      return userCredential;
    } catch (error) {
      throw new Error(toLoginErrorMessage(error));
    }
  }

  function logout() {
    // Security: Clear admin status and sign out
    setIsAdmin(false);
    return signOut(auth).catch(error => {
      console.error('Logout error:', error);
      throw new Error('Възникна грешка при излизане.');
    });
  }

  function resetPassword(email) {
    // Security: Input validation
    if (!email) {
      throw new Error('Моля, въведете имейл адрес.');
    }

    if (!isValidEmail(email)) {
      throw new Error('Невалиден имейл формат.');
    }

    // Security: Prevent password reset for admin account
    if (isAdminEmail(email)) {
      throw new Error('Не можете да възстановите паролата на административен акаунт.');
    }

    return sendPasswordResetEmail(auth, email).catch((error) => {
      throw new Error(toResetPasswordErrorMessage(error));
    });
  }

  // Security: Function to check if current user has access to specific resources
  const hasAccess = (resourceUserId) => {
    if (!currentUser) return false;
    
    // Admin has access to all resources
    if (isAdmin) return true;
    
    // User can only access their own resources
    return currentUser.uid === resourceUserId;
  };

  const value = {
    currentUser,
    isAdmin,
    login,
    register,
    logout,
    resetPassword,
    hasAccess, // New security function
    validateUserSession: isValidUserSession // Expose for additional validation if needed
  };

  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
